import { useState } from 'react';
import { Link } from 'react-router-dom';

interface UserProfileProps {
  userName: string;
  avatarUrl?: string | null;
}

export function UserProfile({ userName, avatarUrl }: UserProfileProps) {
  // A remote avatar that fails to load used to fall through to the browser's
  // alt-text rendering, which painted the full user name inside a 32px circle
  // and clipped it mid-word ("Arya"). Track the failure and fall back to
  // initials, which is what the no-avatar path already does.
  const [imageFailed, setImageFailed] = useState(false);
  const showImage = !!avatarUrl && !imageFailed;

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // A brand disc rather than `bg-black`: a pure-black circle on a near-black
  // page reads as a hole, and the initials had nothing to sit on.
  //
  // 400 at rest, deepening to 500 on hover. At the old 600 the disc was the
  // most saturated object in the bar — a 32px chip of full-strength indigo
  // (#5B59F4) in light, violet (#8846E4) in dark, both literally the
  // `--grid-line` hue at full tilt — and it out-shouted the active-section
  // pill beside it, which is the element that actually carries state.
  //
  // Hover DEEPENS here rather than lightening, which is the opposite of the
  // original. On a soft disc that is the right direction anyway: the darker
  // state reads as pressed-toward-you, and it keeps the whole interaction on
  // the calm end of the ramp instead of blowing out to near-white.
  //
  // The initials are `text-brand-950`, NOT white, and that is forced rather
  // than a taste call. White fails WCAG AA on any of these discs — 3.40:1 on
  // 500 in light, 3.51:1 in dark — and would fail the axe gate on the
  // UserProfile stories. Deep brand ink measures 6.63:1 / 6.54:1 at rest and
  // 4.65:1 / 4.54:1 on hover (light / dark, read off the rendered element).
  // Hover is the tight state — axe only checks the resting one, so a nudge in
  // either colour will break AA somewhere the gate cannot see it.
  const baseClass =
    'flex h-8 w-8 items-center justify-center overflow-hidden rounded-full bg-brand-400 text-sm font-medium text-brand-950 transition-colors hover:bg-brand-500 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background';

  return (
    <Link
      to='/settings'
      className={baseClass}
      aria-label={`Open settings for ${userName}`}
      title='Settings'
    >
      {showImage ? (
        <img
          src={avatarUrl}
          alt=''
          className='h-full w-full object-cover'
          onError={() => setImageFailed(true)}
        />
      ) : (
        getInitials(userName)
      )}
    </Link>
  );
}
